(ns hai.home.mobile.mini-apps.tasks
  "My Tasks mini-app card for the mobile dashboard: the same task data as the
   full Tasks page, grouped into collapsible Now / Next / Later sections. The ＋
   swaps the card into Add-a-task mode and a row swaps it for the task open in
   it; the title links to the full Tasks page. Shares the lanes and the two
   views with the desktop card via [[hai.tasks.mini-app]] and the tinted
   header row via [[hai.tasks.header]]; the card frame stays mobile's own."
  (:require
   [hai.entity.data :as entity-data]
   [hai.entity.sections :as sections]
   [hai.home.mobile.mini-apps :as mini-apps]
   [hai.learn-more :as learn-more]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.mini-apps.frame :as frame]
   [hai.spaces.data :as spaces-data]
   [hai.tasks.add-pill :as add-pill]
   [hai.tasks.data :as data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.mini-app :as tasks-mini-app]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.routing :as routing]
   [humbleai.belt.set :as set]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.how-it-works :as how-it-works]))

;; The frame stays mobile's own — softer shadow, 20px radius, touch-sized
;; targets — but the header row is the shared one every tasks surface uses.
(o/defstyled card mini-apps/card
  ;; The deadline sheet rounds itself to the card's corners.
  {:--tasks-card-radius "20px"}
  ;; Collapsed, the bar is the whole card, so it rounds its bottom corners too.
  [:&.collapsed
   [header/bar
    {:border-bottom-left-radius "20px"
     :border-bottom-right-radius "20px"}]]
  [header/bar
   ;; The mobile frame header's padding, so the actions line up across cards.
   {:padding "0 3px 0 16px"
    :border-top-left-radius "20px"
    :border-top-right-radius "20px"}
   ;; Touch targets.
   [:.bar-action-btn {:width "32px" :height "32px"}]
   [sections/kebab-wrap
    [:button {:width "32px" :height "32px"}]]]
  ;; Rows float as spaced cards rather than a bordered list. The inset holds
  ;; them off the lane headers above and below and the card's sides.
  [:.section-tasks
   {:display "flex" :flex-direction "column" :gap "9px"
    :padding "9px"}]
  ;; The body under the header is one height in every mode — the list, Add a
  ;; task, a task open in the card, even empty or loading — so switching between
  ;; them doesn't move the page: a short list leaves room below it, and a long
  ;; list or form scrolls inside rather than stretching the card. The clip
  ;; rounds the scrollbar into the card's corners.
  [frame/list-clip
   {:height "480px"
    :border-bottom-left-radius "20px"
    :border-bottom-right-radius "20px"}])

;; Links the add/save toasts to the task. Home lists tasks rather than showing
;; one, so the link always has somewhere to go.
(def ^:private task-link
  {:href-fn #(routing/href :tasks/detail {:task/uuid %})
   :target routing/link-target})

(defn panel [{title :mini-app/title}]
  (r/with-let [!open (r/atom data/default-open-bucket-ids)
               toggle-section (fn [id] (swap! !open set/toggle-membership id))]
    (let [buckets @(<s [::data/buckets])
          loaded? @(<s [::data/loaded?])
          adding? @(<s [::data/composer-open?])
          editing-task @(<s [::data/editor-task])
          default-urgency @(<s [::data/default-urgency])
          collapsed? @(<s [::mini-apps-data/collapsed? :tasks])
          open @!open
          total (reduce + (map count (vals buckets)))
          add-task! (fn []
                      (>e [::mini-apps-data/expand :tasks])
                      (>e [::data/open-composer (form/from-task nil default-urgency)]))]
      (cond
        adding?
        [card
         [tasks-mini-app/composer-view
          {:draft-id data/composer-draft-id
           :saving? (= :start @(<s [:fetch/state ::create]))
           :show-hint? false
           :on-save (fn [staged]
                      (>e [::data/create (assoc staged
                                                :request-id ::create
                                                :task-link task-link
                                                :success-fx [[:dispatch [::data/close-composer]]])])
                      (swap! !open conj (data/staged-bucket staged)))
           :on-close #(>e [::data/close-composer])}]]

        editing-task
        [card
         [tasks-mini-app/editing-view
          {:task editing-task
           :spaces? @(<s [::spaces-data/enabled?])
           :on-open-entity entity-data/open-entity
           :on-add add-task!}]]

        :else
        [card {:class (when collapsed? "collapsed")}
         [header/bar
          {:title title
           :href (routing/href :tasks/view)
           :target routing/link-target}
          [add-pill/component
           {:on-add add-task!}]
          [frame/collapse-toggle
           {:collapsed? collapsed?
            :label      "tasks"
            :class      "bar-action-btn"
            :on-toggle  #(>e [::mini-apps-data/toggle-collapsed :tasks])}]
          [learn-more/menu {:url how-it-works/tasks-url :label "Tasks"}]]
         (when-not collapsed?
           [frame/list-clip
            (cond
              (not loaded?) nil
              (zero? total) [:div.app-placeholder "Nothing yet — add your first task above."]
              :else
              [tasks-mini-app/sections
               [tasks-mini-app/lanes
                {:buckets buckets
                 :open open
                 :on-toggle toggle-section
                 :row-opts {:on-open-entity entity-data/open-entity}}]])])]))))
